import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  MapPin, Zap, CheckCircle, Circle, Users, Trophy, Calendar,
  LogOut, User, ChevronRight, Star, TrendingUp, Bell, Shield,
  ArrowRight, BookOpen, Target, Plus,
} from "lucide-react";
import { PromiseMeter, CivicScore } from "../components/dashboard/DashboardWidgets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_COLORS = {
  alp: "#CC2529", liberal: "#1B4F9E", greens: "#10B981", nationals: "#155E3B",
  "one-nation": "#D97706", lnp: "#2563EB", teal: "#0D9488", uap: "#F59E0B",
  jln: "#7C3AED", clp: "#DC2626", "wa-nationals": "#065F46", "sa-best": "#9333EA",
};

const ISSUE_META = {
  housing:   { label: "Housing", color: "#DC2626", icon: "🏠" },
  climate:   { label: "Climate & Energy", color: "#10B981", icon: "⚡" },
  health:    { label: "Health & Hospitals", color: "#7C3AED", icon: "🏥" },
  economy:   { label: "Economy & Cost of Living", color: "#F59E0B", icon: "💰" },
  education: { label: "Education", color: "#2563EB", icon: "📚" },
  transport: { label: "Transport & Infrastructure", color: "#0D9488", icon: "🚆" },
  integrity: { label: "Political Integrity", color: "#6B7280", icon: "⚖️" },
  social:    { label: "Social Policy", color: "#EC4899", icon: "🤝" },
};

const CHECKLIST_ITEMS = [
  { key: "enrolled", label: "Confirmed I'm enrolled to vote", link: "https://www.aec.gov.au/enrolment/" },
  { key: "polling_place", label: "Found my polling place", link: "https://www.aec.gov.au/election/polling-place.htm" },
  { key: "compared_parties", label: "Compared the parties", link: "/compare" },
  { key: "read_candidates", label: "Read my local candidates", link: "/electorate" },
  { key: "told_friends", label: "Shared AusVote with a friend", link: null },
];

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postcode, setPostcode] = useState("");
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [checklistUpdating, setChecklistUpdating] = useState({});

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user/personalized`, { withCredentials: true });
      setData(r.data);
      if (r.data.profile?.postcode) setPostcode(r.data.profile.postcode);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const submitPostcode = async () => {
    if (!/^\d{4}$/.test(postcode.trim())) {
      setPostcodeError("Enter a valid 4-digit Australian postcode");
      return;
    }
    setPostcodeLoading(true);
    setPostcodeError("");
    try {
      await axios.put(`${API}/user/profile`, { postcode: postcode.trim() }, { withCredentials: true });
      await fetchData();
    } catch (err) {
      console.error("Failed to save postcode:", err);
      setPostcodeError("Could not save postcode. Try again.");
    }
    setPostcodeLoading(false);
  };

  const toggleIssue = async (issue) => {
    const current = data?.profile?.my_issues || [];
    const next = current.includes(issue)
      ? current.filter((i) => i !== issue)
      : current.length < 3 ? [...current, issue] : current;
    try {
      await axios.put(`${API}/user/profile`, { my_issues: next }, { withCredentials: true });
      setData((d) => ({ ...d, profile: { ...d.profile, my_issues: next } }));
    } catch (err) {
      console.error("Failed to toggle issue:", err);
    }
  };

  const toggleChecklist = async (item, current) => {
    setChecklistUpdating((prev) => ({ ...prev, [item]: true }));
    try {
      const r = await axios.put(`${API}/user/checklist`, { item, value: !current }, { withCredentials: true });
      setData((d) => ({
        ...d,
        profile: { ...d.profile, checklist: r.data.checklist, civic_score: r.data.civic_score },
      }));
    } catch (err) {
      console.error("Failed to update checklist:", err);
    }
    setChecklistUpdating((prev) => ({ ...prev, [item]: false }));
  };

  if (!user) return null;

  const profile = data?.profile || {};
  const checklist = profile.checklist || {};
  const myIssues = profile.my_issues || [];
  const civicScore = profile.civic_score || 0;
  const checklistDone = CHECKLIST_ITEMS.filter((c) => checklist[c.key]).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="user-dashboard">

      {/* Hero bar */}
      <div className="border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="h-1.5" style={{ backgroundColor: "#FFC82E" }} />
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full border-2 border-black flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 border-2 border-black flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#FFC82E" }}>
                <User className="w-5 h-5 text-black" />
              </div>
            )}
            <div>
              <p className="text-white font-black text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
                {profile.onboarding_complete && profile.federal_electorate
                  ? `${user.name} · ${profile.federal_electorate}, ${profile.state_code}`
                  : `Welcome, ${user.name}`}
              </p>
              <p className="text-xs" style={{ color: "#94A3B8" }}>{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="hidden sm:flex items-center gap-1 text-xs font-black px-3 py-2 border-2 border-white text-white hover:opacity-75"
            >
              Home
            </button>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#C04E3F", color: "#fff" }}
            >
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 border-2 border-black animate-pulse" style={{ backgroundColor: surface }} />
            ))}
          </div>
        ) : (
          <>
            {/* ── Top row: Civic Score + Electorate Setup ─────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dashboard-top-row">
              <CivicScore score={civicScore} />

              {/* Electorate Setup */}
              <div className="border-2 border-black p-4" style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }} data-testid="electorate-setup">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4" style={{ color: "#FFC82E" }} />
                  <p className="font-black text-sm" style={{ color: text }}>
                    {profile.federal_electorate ? "Your Electorate" : "Set Your Postcode"}
                  </p>
                </div>
                {profile.federal_electorate ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-black px-2 py-0.5 border-2 border-black text-white" style={{ backgroundColor: "#1F2937" }}>
                        FEDERAL
                      </span>
                      <span className="font-black text-base" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                        {profile.federal_electorate}
                      </span>
                      <span className="text-xs font-bold" style={{ color: textSec }}>
                        {profile.state_code}
                      </span>
                    </div>
                    {data?.next_state_election && (
                      <div className="text-xs mb-3" style={{ color: textSec }}>
                        Next state election: <span className="font-black" style={{ color: text }}>{data.next_state_election.name}</span> · {data.next_state_election.date}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        className="border-2 border-black px-2 py-1 text-xs w-24"
                        style={{ backgroundColor: isDark ? "#0F172A" : "#F9F7F2", color: text }}
                        placeholder="Update postcode"
                        value={postcode}
                        onChange={(e) => setPostcode(e.target.value)}
                        maxLength={4}
                      />
                      <button
                        onClick={submitPostcode}
                        disabled={postcodeLoading}
                        className="text-xs font-black px-3 py-1 border-2 border-black hover:-translate-y-0.5 transition-transform"
                        style={{ backgroundColor: "#FFC82E", color: "#000" }}
                        data-testid="update-postcode-btn"
                      >
                        Update
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs mb-3" style={{ color: textSec }}>
                      Enter your postcode to see YOUR federal electorate, local MP, and upcoming elections.
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        className="border-2 border-black px-2 py-1.5 text-sm flex-1"
                        style={{ backgroundColor: isDark ? "#0F172A" : "#F9F7F2", color: text }}
                        placeholder="e.g. 3000"
                        value={postcode}
                        onChange={(e) => setPostcode(e.target.value)}
                        maxLength={4}
                        data-testid="postcode-input"
                        onKeyDown={(e) => e.key === "Enter" && submitPostcode()}
                      />
                      <button
                        onClick={submitPostcode}
                        disabled={postcodeLoading}
                        className="text-sm font-black px-4 py-1.5 border-2 border-black hover:-translate-y-0.5 transition-transform"
                        style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0 #000" }}
                        data-testid="submit-postcode-btn"
                      >
                        {postcodeLoading ? "…" : "Find My Electorate"}
                      </button>
                    </div>
                    {postcodeError && <p className="text-xs mt-1" style={{ color: "#EF4444" }}>{postcodeError}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* ── Your MP ─────────────────────────────────────────────── */}
            {data?.your_mp && (
              <section data-testid="your-mp-section">
                <h2 className="text-xl font-black mb-3 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                  <Shield className="w-5 h-5" style={{ color: "#FFC82E" }} />
                  Your Federal MP — {profile.federal_electorate}
                </h2>
                <div
                  className="border-2 border-black p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                  style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="w-12 h-12 flex-shrink-0 border-2 border-black flex items-center justify-center"
                      style={{ backgroundColor: data.your_mp.party_color + "20" }}
                    >
                      <User className="w-6 h-6" style={{ color: data.your_mp.party_color }} />
                    </div>
                    <div>
                      <p className="font-black text-base" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{data.your_mp.name}</p>
                      <span className="text-xs font-black px-2 py-0.5 border border-current" style={{ color: data.your_mp.party_color }}>
                        {data.your_mp.party_name}
                      </span>
                      <p className="text-xs mt-1" style={{ color: textSec }}>{data.your_mp.role}</p>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black mb-1" style={{ color: textSec }}>
                      Promise delivery: {data.your_mp.promises_delivered}/{data.your_mp.promises_total}
                    </p>
                    <PromiseMeter pct={data.your_mp.delivery_pct} color={data.your_mp.party_color} />
                  </div>
                  <button
                    onClick={() => navigate(`/politicians/${data.your_mp.id}`)}
                    className="flex items-center gap-1.5 text-xs font-black px-3 py-2 border-2 border-black hover:-translate-y-0.5 transition-transform flex-shrink-0"
                    style={{ backgroundColor: data.your_mp.party_color, color: "#fff", boxShadow: "2px 2px 0 #000" }}
                    data-testid="view-your-mp-btn"
                  >
                    View Profile <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </section>
            )}

            {/* ── Followed Politicians ─────────────────────────────────── */}
            <section data-testid="followed-politicians-section">
              <h2 className="text-xl font-black mb-3 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                <Star className="w-5 h-5" style={{ color: "#FFC82E" }} />
                Politicians You're Watching ({(data?.followed_politicians || []).length})
              </h2>
              {(!data?.followed_politicians || data.followed_politicians.length === 0) ? (
                <div className="border-2 border-black border-dashed p-6 text-center" style={{ backgroundColor: surface }}>
                  <p className="text-sm mb-1 font-bold" style={{ color: text }}>No politicians followed yet</p>
                  <p className="text-xs mb-4" style={{ color: textSec }}>Click "Follow" on any politician's profile to track their votes and promises here.</p>
                  <button
                    onClick={() => navigate("/politicians")}
                    className="text-xs font-black px-4 py-2 border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: "#1F2937", color: "#fff", boxShadow: "2px 2px 0 #000" }}
                  >
                    Browse Politicians
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.followed_politicians.map((p) => (
                    <div
                      key={p.id}
                      className="border-2 border-black p-3"
                      style={{ backgroundColor: surface, boxShadow: "2px 2px 0 #000" }}
                      data-testid={`followed-pol-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="font-black text-sm" style={{ color: text }}>{p.name}</p>
                          <span className="text-xs font-black px-1.5 py-0.5 border border-current" style={{ color: p.party_color }}>{p.party_name}</span>
                        </div>
                        <button
                          onClick={() => navigate(`/politicians/${p.id}`)}
                          className="text-xs font-black px-2 py-1 border-2 border-black hover:-translate-y-0.5 transition-transform flex-shrink-0"
                          style={{ backgroundColor: p.party_color, color: "#fff" }}
                        >
                          View
                        </button>
                      </div>
                      <p className="text-xs mb-1" style={{ color: textSec }}>
                        Promises: {p.promises_delivered}/{p.promises_total} delivered
                      </p>
                      <PromiseMeter pct={p.delivery_pct} color={p.party_color} />
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => navigate("/politicians")}
                className="mt-3 flex items-center gap-1.5 text-xs font-black hover:opacity-75"
                style={{ color: "#FFC82E" }}
              >
                <Plus className="w-3.5 h-3.5" /> Follow more politicians
              </button>
            </section>

            {/* ── My Issues ───────────────────────────────────────────── */}
            <section data-testid="my-issues-section">
              <h2 className="text-xl font-black mb-1 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                <Target className="w-5 h-5" style={{ color: "#FFC82E" }} />
                My Issues
              </h2>
              <p className="text-xs mb-4" style={{ color: textSec }}>
                Select up to 3 issues — your dashboard and news will be personalised to these.
                {myIssues.length >= 3 && " (Deselect one to change)"}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(ISSUE_META).map(([key, meta]) => {
                  const selected = myIssues.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleIssue(key)}
                      disabled={!selected && myIssues.length >= 3}
                      className="flex items-center gap-2 p-3 border-2 border-black text-left hover:-translate-y-0.5 transition-transform disabled:opacity-40"
                      style={{
                        backgroundColor: selected ? meta.color : surface,
                        color: selected ? "#fff" : text,
                        boxShadow: selected ? "2px 2px 0 #000" : "none",
                      }}
                      data-testid={`issue-toggle-${key}`}
                    >
                      <span className="text-base">{meta.icon}</span>
                      <span className="text-xs font-black leading-tight">{meta.label}</span>
                      {selected && <CheckCircle className="w-3 h-3 ml-auto flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {myIssues.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {myIssues.map((issue) => {
                    const m = ISSUE_META[issue];
                    return (
                      <button
                        key={issue}
                        onClick={() => navigate(`/issues`)}
                        className="flex items-center gap-1.5 text-xs font-black px-3 py-1.5 border-2 border-black hover:-translate-y-0.5 transition-transform"
                        style={{ backgroundColor: m.color, color: "#fff", boxShadow: "2px 2px 0 #000" }}
                      >
                        {m.icon} {m.label} <ArrowRight className="w-3 h-3" />
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ── Election Readiness Checklist ─────────────────────────── */}
            <section data-testid="checklist-section">
              <h2 className="text-xl font-black mb-1 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                <CheckCircle className="w-5 h-5" style={{ color: "#10B981" }} />
                Election Readiness Checklist
              </h2>
              <p className="text-xs mb-4" style={{ color: textSec }}>
                {checklistDone}/{CHECKLIST_ITEMS.length} complete
              </p>
              <div className="space-y-2">
                {CHECKLIST_ITEMS.map((item) => {
                  const done = !!checklist[item.key];
                  const updating = checklistUpdating[item.key];
                  return (
                    <div
                      key={item.key}
                      className="flex items-center gap-3 border-2 border-black p-3"
                      style={{ backgroundColor: done ? (isDark ? "#0D2818" : "#F0FDF4") : surface }}
                      data-testid={`checklist-${item.key}`}
                    >
                      <button
                        onClick={() => toggleChecklist(item.key, done)}
                        disabled={updating}
                        className="flex-shrink-0 hover:opacity-75 transition-opacity"
                      >
                        {done
                          ? <CheckCircle className="w-5 h-5" style={{ color: "#10B981" }} />
                          : <Circle className="w-5 h-5" style={{ color: textSec }} />}
                      </button>
                      <p className="flex-1 text-sm font-bold" style={{ color: done ? "#10B981" : text, textDecoration: done ? "line-through" : "none" }}>
                        {item.label}
                      </p>
                      {item.link && !done && (
                        <button
                          onClick={() => item.link.startsWith("http") ? window.open(item.link, "_blank") : navigate(item.link)}
                          className="text-xs font-black flex items-center gap-1 hover:opacity-75"
                          style={{ color: "#FFC82E" }}
                        >
                          Do it <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── Quiz History ─────────────────────────────────────────── */}
            <section data-testid="quiz-history-section">
              <h2 className="text-xl font-black mb-3 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                <Trophy className="w-5 h-5" style={{ color: "#FFC82E" }} />
                Party Match Quiz History
              </h2>
              {(!data?.quiz_history || data.quiz_history.length === 0) ? (
                <div className="border-2 border-black border-dashed p-6 text-center" style={{ backgroundColor: surface }}>
                  <p className="text-sm mb-3" style={{ color: textSec }}>Take the quiz to see how you match with Australian parties.</p>
                  <button
                    onClick={() => navigate("/quiz")}
                    className="text-sm font-black px-4 py-2 border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0 #000" }}
                  >
                    Take the Quiz
                  </button>
                </div>
              ) : (
                <div>
                  {/* Trend bars */}
                  <div className="flex items-end gap-2 mb-4 overflow-x-auto pb-1">
                    {[...data.quiz_history].reverse().map((r) => {
                      const color = PARTY_COLORS[r.top_party_id] || "#6B7280";
                      return (
                        <div key={`quiz-bar-${r.created_at || r.top_party_id}`} className="flex flex-col items-center gap-1 min-w-[40px]">
                          <span className="text-xs font-black" style={{ color }}>{r.top_percentage}%</span>
                          <div
                            className="w-8 border-2 border-black"
                            style={{ height: `${r.top_percentage * 0.8}px`, backgroundColor: color }}
                          />
                          <span className="text-xs" style={{ color: textSec }}>{r.created_at?.slice(5, 10)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="space-y-2">
                    {data.quiz_history.slice(0, 5).map((r, i) => (
                      <div
                        key={`quiz-result-${r.created_at || r.top_party_id}`}
                        className="border-2 border-black p-3 flex items-center gap-3"
                        style={{ backgroundColor: surface }}
                        data-testid={`quiz-result-${i}`}
                      >
                        <div
                          className="w-10 h-10 flex items-center justify-center border-2 border-black flex-shrink-0 text-xs font-black text-white"
                          style={{ backgroundColor: PARTY_COLORS[r.top_party_id] || "#6B7280" }}
                        >
                          {r.top_party_id?.toUpperCase().slice(0, 3)}
                        </div>
                        <div className="flex-1">
                          <p className="font-black text-sm" style={{ color: text }}>{r.top_percentage}% match with {r.top_party_id?.toUpperCase()}</p>
                          <p className="text-xs" style={{ color: textSec }}>{r.created_at?.slice(0, 10)}</p>
                        </div>
                        {i === 0 && <span className="text-xs font-black px-2 py-0.5 border-2 border-black" style={{ backgroundColor: "#FFC82E" }}>LATEST</span>}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate("/quiz")} className="mt-3 text-xs font-black hover:opacity-75 flex items-center gap-1" style={{ color: "#FFC82E" }}>
                    Retake quiz <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </section>

            {/* ── Upcoming Elections ───────────────────────────────────── */}
            <section data-testid="upcoming-elections-dash-section">
              <h2 className="text-xl font-black mb-3 flex items-center gap-2" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                <Calendar className="w-5 h-5" style={{ color: "#FFC82E" }} />
                Upcoming Elections
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ...(data?.next_state_election ? [{ ...data.next_state_election, type: "State" }] : []),
                  { name: "Federal Election", date: "2028-05-17", path: null, type: "Federal" },
                  { name: "SA State Election 2026", date: "2026-03-21", path: "/sa-2026", type: "State" },
                  { name: "VIC State Election 2026", date: "2026-11-28", path: "/vic-2026", type: "State" },
                ].slice(0, 4).map((e) => {
                  const days = Math.floor((new Date(e.date) - Date.now()) / 86400000);
                  return (
                    <div
                      key={e.name}
                      className="border-2 border-black p-3 flex items-center justify-between gap-3"
                      style={{ backgroundColor: surface }}
                    >
                      <div>
                        <p className="font-black text-sm" style={{ color: text }}>{e.name}</p>
                        <p className="text-xs" style={{ color: textSec }}>{e.date} · {e.type}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {days > 0 && (
                          <span className="text-xs font-black px-2 py-0.5 border-2 border-black" style={{ backgroundColor: "#FFC82E" }}>
                            {days}d
                          </span>
                        )}
                        {e.path && (
                          <button
                            onClick={() => navigate(e.path)}
                            className="text-xs font-black hover:opacity-75"
                            style={{ color: "#FFC82E" }}
                          >
                            Guide →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── Quick Access ─────────────────────────────────────────── */}
            <section>
              <h2 className="text-xl font-black mb-3" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>Quick Access</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Take Quiz", path: "/quiz", color: "#2E5948" },
                  { label: "Compare Parties", path: "/compare", color: "#1B4F9E" },
                  { label: "News Feed", path: "/news", color: "#D97706" },
                  { label: "AI Chat", path: "/chat", color: "#9333EA" },
                  { label: "Leaderboard", path: "/leaderboard", color: "#CC2529" },
                  { label: "Fact Checker", path: "/fact-check", color: "#1F2937" },
                  { label: "VIC 2026", path: "/vic-2026", color: "#1F2937" },
                  { label: "SA 2026", path: "/sa-2026", color: "#CC2529" },
                ].map((l) => (
                  <button
                    key={l.path}
                    onClick={() => navigate(l.path)}
                    className="py-3 font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: l.color, color: "#fff", boxShadow: "2px 2px 0 #000" }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
